// Navi App Configuration

// Automatische Umgebungserkennung
const isLocalDevelopment =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1" ||
  (window.location.hostname.includes("localhost") && window.location.port);

// Production check für Server
const isProduction =
  window.location.hostname.includes(".vs-wbg.de") ||
  window.location.hostname.includes("navi.vs-wbg.de") ||
  (!isLocalDevelopment && window.location.protocol === "https:");

// API Base URLs
export const API_CONFIG = {
  // Lokal: Mock/Demo-Modus (keine echte DB)
  local: {
    baseURL: "",
    useMockData: true,
    enableOfflineMode: true,
  },

  // Production: Echter Server mit PHP-API
  production: {
    baseURL: "/api",
    useMockData: false,
    enableOfflineMode: false,
  },
};

// Aktuelle Konfiguration basierend auf Umgebung
const currentConfig = isLocalDevelopment
  ? API_CONFIG.local
  : API_CONFIG.production;

// Exportiere erweiterte Config
export const config = {
  ...currentConfig,
  isDevelopment: isLocalDevelopment,
  isProduction: isProduction,
  getApiUrl: (endpoint) => {
    if (isLocalDevelopment) {
      // Im Mock-Modus geben wir nur den Endpoint zurück
      return endpoint;
    }
    // Für Production verwende /api/endpoints/ Pfad mit .php Extension
    // Entferne führendes /api falls vorhanden und füge .php hinzu
    const cleanEndpoint = endpoint.replace(/^\/api/, "");
    return `${currentConfig.baseURL}/endpoints${cleanEndpoint}.php`;
  },
};

// Mock-Daten für lokale Entwicklung
export const MOCK_DATA = {
  incidents: [
    {
      id: 1,
      lat: 52.52,
      lng: 13.405,
      type: "traffic_jam",
      description: "Stau auf der A100 Richtung Stadtmitte",
      severity: "high",
      created_at: new Date().toISOString(),
    },
    {
      id: 2,
      lat: 48.1351,
      lng: 11.582,
      type: "construction",
      description: "Baustelle auf der B2R - Spur gesperrt",
      severity: "medium",
      created_at: new Date().toISOString(),
    },
    {
      id: 3,
      lat: 50.9375,
      lng: 6.9603,
      type: "accident",
      description: "Unfall auf der A4 - Rettungskräfte vor Ort",
      severity: "critical",
      created_at: new Date().toISOString(),
    },
  ],

  geocoding: {
    results: [
      {
        id: 1,
        display_name: "Berlin, Deutschland",
        lat: 52.52,
        lng: 13.405,
        type: "city",
      },
      {
        id: 2,
        display_name: "München, Deutschland",
        lat: 48.1351,
        lng: 11.582,
        type: "city",
      },
      {
        id: 3,
        display_name: "Hamburg, Deutschland",
        lat: 53.5511,
        lng: 9.9937,
        type: "city",
      },
      {
        id: 4,
        display_name: "Köln, Deutschland",
        lat: 50.9375,
        lng: 6.9603,
        type: "city",
      },
      {
        id: 5,
        display_name: "Frankfurt am Main, Deutschland",
        lat: 50.1109,
        lng: 8.6821,
        type: "city",
      },
    ],
  },

  routing: {
    distance: 584000, // 584 km in Metern
    duration: 18600, // 5h 10min in Sekunden
    coordinates: [
      [52.52, 13.405], // Berlin
      [52.4, 13.0],
      [52.0, 12.5],
      [51.5, 12.0],
      [51.0, 11.5],
      [50.5, 11.3],
      [49.8, 11.4],
      [49.2, 11.5],
      [48.8, 11.6],
      [48.1351, 11.582], // München
    ],
    instructions: [
      { text: "Fahren Sie auf die A9 Richtung München", distance: 50000 },
      { text: "Folgen Sie der A9 für 500 km", distance: 500000 },
      { text: "Nehmen Sie die Ausfahrt München-Zentrum", distance: 34000 },
      { text: "Sie haben Ihr Ziel erreicht", distance: 0 },
    ],
    start: { lat: 52.52, lng: 13.405 },
    end: { lat: 48.1351, lng: 11.582 },
  },

  traffic: [
    {
      id: 1,
      road_name: "A100 Berlin",
      speed: 25,
      status: "slow",
    },
    {
      id: 2,
      road_name: "A9 München",
      speed: 80,
      status: "normal",
    },
    {
      id: 3,
      road_name: "A1 Hamburg",
      speed: 15,
      status: "jam",
    },
  ],
};

// API Client mit Mock-Unterstützung
export class ApiClient {
  constructor() {
    this.baseURL = config.baseURL;
    this.useMockData = config.useMockData;
  }

  async request(endpoint, options = {}) {
    // Mock-Modus für lokale Entwicklung
    if (this.useMockData) {
      return this.handleMockRequest(endpoint, options);
    }

    // Echter API-Aufruf für Production - verwende getApiUrl
    const url = config.getApiUrl(endpoint);
    const response = await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }

    return response.json();
  }

  handleMockRequest(endpoint, options) {
    console.log("Mock API Call:", endpoint, options);

    // Simuliere Netzwerk-Delay
    return new Promise((resolve) => {
      setTimeout(() => {
        if (endpoint.includes("/incidents") && options.method === "POST") {
          // Neuen Incident hinzufügen (Mock)
          resolve({
            success: true,
            message: "Incident wurde gemeldet (Demo-Modus)",
            id: Date.now(),
          });
        } else if (endpoint.includes("/incidents")) {
          resolve({
            success: true,
            incidents: MOCK_DATA.incidents,
            count: MOCK_DATA.incidents.length,
          });
        } else if (endpoint.includes("/geocoding")) {
          const body = options.body ? JSON.parse(options.body) : {};
          const query = body.address || "";
          const results = MOCK_DATA.geocoding.results.filter((item) =>
            item.display_name.toLowerCase().includes(query.toLowerCase())
          );
          resolve({
            success: true,
            results: results.slice(0, 5),
            count: results.length,
          });
        } else if (endpoint.includes("/routing")) {
          const body = options.body ? JSON.parse(options.body) : {};
          resolve({
            success: true,
            route: {
              ...MOCK_DATA.routing,
              start_address: body.start || "Startpunkt",
              end_address: body.end || "Zielpunkt",
            },
          });
        } else if (endpoint.includes("/traffic")) {
          resolve({
            success: true,
            data: MOCK_DATA.traffic,
            count: MOCK_DATA.traffic.length,
          });
        } else if (endpoint.includes("/health")) {
          resolve({
            status: "OK",
            timestamp: new Date().toISOString(),
            version: "1.0.0",
            backend: "Mock",
          });
        } else {
          resolve({ success: true, data: [] });
        }
      }, 300); // 300ms Delay
    });
  }
}

export const api = new ApiClient();

// Global fetch override nur für lokale Entwicklung
if (config.useMockData && isLocalDevelopment) {
  const originalFetch = window.fetch;
  window.fetch = async (url, options = {}) => {
    // Wenn es ein API-Call ist, benutze unseren Mock-Handler
    if (
      typeof url === "string" &&
      (url.startsWith("/api") || url.includes("endpoints"))
    ) {
      return {
        ok: true,
        json: () => api.handleMockRequest(url, options),
      };
    }
    // Ansonsten normaler fetch (für externe Ressourcen wie Leaflet)
    return originalFetch(url, options);
  };
}
